/**
 * The ONE namespace-name rule for both storage twins — `@puffin-core/storage`'s `StorageService` and
 * `@puffin-core/storage-native`'s `NativeStorageService` (MUTATION-BURNDOWN-001, coordinator ruling 2026-10-24).
 *
 * Each twin's `getNamespace(name)` stores a namespaced key as `<name>_<key>`. A NAME that holds `_` therefore shares
 * keys with another namespace: `getNamespace('auth').set('token_x')` and `getNamespace('auth_token').get('x')` both
 * address `auth_token_x`, so one namespace reads and overwrites the other's values. A name with no `_` makes a stored
 * key's FIRST `_` the end of its namespace, so two different names can never meet. Refusing `_` in the NAME closes the
 * collision without changing any stored key: the census of 2026-10-24 found no production name containing `_`.
 *
 * One rule for both twins, so the browser and the native app cannot judge a name differently. Zero dependencies;
 * dual-use: CJS source with a GENERATED ESM re-export.
 *
 * @module @puffin-core/storage-namespace-name
 */

'use strict';

/** The character a namespace joins its name to its keys with — and so the one a name may not hold. */
const NAMESPACE_JOINER = '_';

/**
 * Refuse a namespace name the joiner would make ambiguous.
 *
 * @param {string} owner - The storage service judging the name (named in the refusal).
 * @param {*} name - The namespace name.
 * @returns {string} The name, unchanged.
 * @throws {TypeError} When the name is not a non-empty string, or contains the joiner.
 */
function assertNamespaceName(owner, name) {
  if (typeof name !== 'string' || name.length === 0) {
    throw new TypeError(`${owner}: a namespace name must be a non-empty string`);
  }
  if (name.includes(NAMESPACE_JOINER)) {
    throw new TypeError(
      `${owner}: a namespace name may not contain "${NAMESPACE_JOINER}" ("${name}") — "${NAMESPACE_JOINER}" joins a namespace to its keys, so a name holding it would share keys with another namespace`,
    );
  }
  return name;
}

module.exports = { NAMESPACE_JOINER, assertNamespaceName };
